import './KeyBenefitsSection.scss';

import React, { memo, useRef } from 'react';

import {
  Box,
  Button,
  Carousel,
  CarouselSlide,
  Column,
  FlexBox,
  FluidImage,
  GridContainer,
  H2,
  H3,
  Hidden,
  Icon,
  P,
  Row,
  Visible,
} from '@vp/swan';

import {
  FeatureGroupModel,
  FeatureModel,
} from '@vp/digital-common-data-model-lib';

import MissingContentAlert from '../../common/MissingContentAlert';

type KeyBenefitsSectionProps = {
  content: FeatureGroupModel;
  isWebsShutdown?: boolean;
  handleGetStartedNavigation?: () => void;
  accessibility: FeatureGroupModel;
};

type IconType = React.ComponentProps<typeof Icon>['iconType'];

const KeyBenefitsSection = ({
  content,
  isWebsShutdown,
  handleGetStartedNavigation,
  accessibility,
}: KeyBenefitsSectionProps) => {
  const features = useRef({
    tailoredToSmallBusinesses: content?.features?.[0],
    expertHelpGuidance: content?.features?.[1],
    growWithTrustedServices: content?.features?.[2],
    anAllInOneExperience: content?.features?.[3],
  });

  if (!content) {
    return <MissingContentAlert />;
  }

  const Feature = memo(({ feature }: { feature: FeatureModel }) => {
    const iconType: IconType = feature.mediaName as IconType;
    return (
      <Box
        p={6}
        backgroundColor="standard"
        className="wix-websites-pdp__benefits__feature-container"
      >
        <FlexBox alignItems="center">
          <Icon iconType={iconType} mr={3} />

          <H3 fontSize="large" m={0}>
            {feature.title}
          </H3>
        </FlexBox>

        <P fontSize="small" mb={0}>
          {feature.description}
        </P>
      </Box>
    );
  });

  return (
    <>
      <GridContainer>
        <Row>
          <Column span={isWebsShutdown ? 10 : 12}>
            <H2 mb={0} fontSize="x2large">
              {content.title}
            </H2>

            <P mb={0}>{content.description}</P>
          </Column>

          {isWebsShutdown && (
            <Column span={2} textAlign="right">
              <Hidden xs>
                <Button
                  style={{ wordBreak: 'normal' }}
                  iconPosition="right"
                  skin="primary"
                  onClick={() => {
                    handleGetStartedNavigation();
                  }}
                >
                  {content.media[1].title}
                  <Icon iconType="arrowRight" size="20p" />
                </Button>
              </Hidden>
            </Column>
          )}
        </Row>

        <Row mt={7}>
          <Column
            span={12}
            spanXs={12}
            spanSm={6}
            spanMd={7}
            spanLg={7}
            spanXl={7}
          >
            <Visible xs>
              <Carousel
                initialSlide={0}
                mb={8}
                gridGutters
                progressIndicator="dots"
                slidesToShow={1.15}
                arrows={false}
                accessibleTextForDots={
                  content.features.length > 0
                    ? content.features.map(
                        (feature, index) =>
                          accessibility.features[5]?.title.replace(
                            '{{SLIDE_NUMBER}}',
                            String(index + 1)
                          ) ?? ''
                      )
                    : []
                }
                accessibleTextForVisibleSlides={
                  accessibility.features[2]?.valueText
                }
                accessibleTextForSlide={accessibility.features[2]?.altText}
                accessibleTextForNext={accessibility.features[4].title}
                accessibleTextForPrevious={accessibility.features[3].title}
                aria-roledescription={accessibility.features[0].title}
                aria-label={content.title}
              >
                {content.features.map((feature: any, index: React.Key) => (
                  <CarouselSlide
                    key={index}
                    className="wix-websites-pdp__slide"
                    aria-roledescription={accessibility.features[1].title}
                  >
                    <Feature feature={feature} />
                  </CarouselSlide>
                ))}
              </Carousel>
            </Visible>

            <Hidden
              xs
              className="wix-websites-pdp__benefits__features-container"
            >
              <Row>
                <Column span={5} spanSm={12} spanMd={6}>
                  <Feature
                    feature={features.current.tailoredToSmallBusinesses}
                  />
                </Column>

                <Column span={7} spanSm={12} spanMd={6}>
                  <Feature feature={features.current.expertHelpGuidance} />
                </Column>

                <Column span={7} spanSm={12} spanMd={6}>
                  <Feature feature={features.current.growWithTrustedServices} />
                </Column>

                <Column span={5} spanSm={12} spanMd={6}>
                  <Feature feature={features.current.anAllInOneExperience} />
                </Column>
              </Row>
            </Hidden>
          </Column>

          <Column
            span={12}
            spanXs={12}
            spanSm={6}
            spanMd={5}
            spanLg={5}
            spanXl={5}
          >
            <FluidImage
              alt={content.media[0].altText}
              srcSet={content.media[0].icons[0].resourceSetUrl}
              src={content.media[0].icons[0].secureUrl}
              sizes="(max-width: 768px) 100vw, 
                (max-width: 1024px) 50vw, 
                (max-width: 3294px) 40vw, 
                1647px"
              className="wix-websites-pdp__benefits__image"
            />
          </Column>
        </Row>
      </GridContainer>
    </>
  );
};

export default KeyBenefitsSection;
